import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupSheetSingleSheetComponent from 'webapp/components/custom-query/setup/sheet/single-sheet';
import FormInputComponent from 'webapp/components/form/input';

import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type DataSheet from 'webapp/models-custom/data-sheet';

interface Args {
	model: CustomMetricSetupFormSheet;
}

export interface ColorSelection {
	color: string;
	range: string;
}

export default class CustomQuerySetupSheetPreviewComponent extends Component<Args> {
	@tracked declare activeSheet: DataSheet;
	@tracked selectedCellValue = ''; // shown in the line above sheet data table

	skipCustomBorders = false;

	// set by the subclasses that need them (sheet/data, wizard/sheet/preview); the template passes them on
	colHeaderFormatter?(colIdx: number): string;
	markSheetDataFocused?(): void;
	selectionEnd?(a1: string): void;
	selectedValueChange?(cellValue: string): void;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setInitialActiveSheet();
	}

	get allSheets(): DataSheet[] {
		const sheets = this.args.model.sheets || [];
		next(() => {
			// for some reason sometimes sheets are set later
			if (!this.activeSheet && sheets.length) {
				this.setInitialActiveSheet();
			}
		});
		return sheets;
	}

	get isMultipleSheets(): boolean {
		return this.allSheets.length > 1;
	}

	get activeColorSelections(): ColorSelection[] {
		return [];
	}

	setInitialActiveSheet(): void {
		let sheet = this.allSheets.find((item) => item.isActive);
		if (!sheet) {
			sheet = this.allSheets[0];
		}
		if (sheet) {
			this.setActiveSheet(sheet);
		}
	}

	setActiveSheet(sheet: DataSheet): void {
		next(() => {
			// avoid same runloop issue
			this.allSheets.forEach((s: DataSheet) => {
				s.isActive = s === sheet;
			});
		});

		this.activeSheet = sheet;
		this.selectedCellValue = '';
	}

	@action
	selectSheet(sheet: DataSheet): void {
		if (sheet !== this.activeSheet) {
			this.setActiveSheet(sheet);
		}
	}
	<template>
		<div id="preview-sheets">
			<div id="sheet-selected-cell">
				<FormInputComponent readonly="true" @value={{this.selectedCellValue}} />
			</div>

			{{#if this.activeSheet}}
				<CustomQuerySetupSheetSingleSheetComponent
					@model={{this.activeSheet}}
					@isMultipleSheets={{this.isMultipleSheets}}
					@colorSelections={{this.activeColorSelections}}
					@skipCustomBorders={{this.skipCustomBorders}}
					@colHeaderFormatter={{this.colHeaderFormatter}}
					@onSheetDataFocused={{this.markSheetDataFocused}}
					@onSelectionEnd={{this.selectionEnd}}
					@onSelectedValueChange={{this.selectedValueChange}}
				/>
			{{/if}}

			{{#if this.activeSheet.isTrimmed}}
				<p id="trimmed-info" class="trimmed-info">
					<CommonUiIconComponent @name="InfoFill" @size="sm" />
					Displaying only the first 500 rows of data for this spreadsheet.
				</p>
			{{/if}}

			{{#if this.isMultipleSheets}}
				<div id="sheet-switch">
					<ul>
						{{#each this.allSheets as |sheet|}}
							<li class={{if sheet.isActive "active"}}>
								<a
									role="button"
									{{on "click" (fn this.selectSheet sheet)}}
								>{{sheet.id}}
								</a>
							</li>
						{{/each}}
					</ul>
				</div>
			{{/if}}
		</div>
	</template>
}
